import {
  Activity,
  ArrowRight,
  BarChart3,
  BookOpenCheck,
  Building2,
  ClipboardCheck,
  FileText,
  LogOut,
  Languages,
  LibraryBig,
  QrCode,
  ShieldCheck,
  Shirt,
  UsersRound,
} from 'lucide-react';
import StudioBrand from './StudioBrand';

const roleLabels = {
  platform_admin: 'Platform Admin',
  trust_admin: 'Trust Admin',
  translator: 'Translator',
  content_reviewer: 'Clinical Reviewer',
};

function TaskCard({ number, icon: Icon, title, description, actions = [], note = '' }) {
  return <article className="studio-home-task">
    <div className="studio-home-task-number">{number}</div>
    <div className="studio-home-task-body">
      <span className="studio-home-task-icon"><Icon size={22}/></span>
      <h2>{title}</h2>
      <p>{description}</p>
      {note && <small>{note}</small>}
      <div className="studio-home-task-actions">
        {actions.map((action) => <button
          className={action.primary ? 'button primary' : 'button secondary'}
          key={action.label}
          type="button"
          onClick={() => action.onClick?.()}
          disabled={action.disabled}
        >
          {action.label}<ArrowRight size={16}/>
        </button>)}
      </div>
    </div>
  </article>;
}

function TrustAdminTasks({ navigate, allowEasyRead = false }) {
  return <section className="studio-home-section">
    <div className="studio-home-section-heading">
      <p className="eyebrow">Trust setup</p>
      <h2>Set up your Trust for patients</h2>
      <p>Work through these tasks in order. You can return to this page at any time.</p>
    </div>
    <div className="studio-home-task-grid">
      <TaskCard number="1" icon={BookOpenCheck} title="Confirm roles and uniforms"
        description="Choose which staff roles patients should see and confirm how each role is recognised locally."
        actions={[
          { label: 'Confirm roles & uniforms', primary: true, onClick: () => navigate('library') },
          { label: 'Create new uniform', onClick: () => navigate('uniforms') },
        ]}/>
      <TaskCard number="2" icon={Building2} title="Add hospital information"
        description="Add the approved local links patients need in Know Your Hospital."
        actions={[{ label: 'Add hospital information', primary: true, onClick: () => navigate('hospital-info') }]}/>
      <TaskCard number="3" icon={LibraryBig} title="Configure Journeys"
        description="Choose the Hospital Journeys your Trust will use and add the relevant local information."
        actions={[{ label: 'Configure Journeys', primary: true, onClick: () => navigate('journeys') }]}/>
      <TaskCard number="4" icon={FileText} title="Print PDF versions of Journeys"
        description="Prepare patient-facing print versions for local hospital review: Easy Read, large text or standard Journey PDFs."
        actions={[
          ...(allowEasyRead ? [{ label: 'Easy Read PDFs', onClick: () => navigate('easy-read-print') }] : []),
          { label: 'Large text PDFs', onClick: () => navigate('large-text-print') },
          { label: 'Standard Journey PDFs', primary: true, onClick: () => navigate('standard-journey-print') },
        ]}
        note="Review the layout and content before printing or distributing any PDF."/>
      <TaskCard number="5" icon={Activity} title="Check readiness for pilot"
        description="See what is complete, what still needs attention and when the Trust configuration is ready to submit."
        actions={[{ label: 'Check pilot readiness', primary: true, onClick: () => navigate('readiness') }]}/>
      <TaskCard number="6" icon={BarChart3} title="Pilot evaluation tools"
        description="Use patient and staff feedback tools, evaluation evidence and the pilot KPI framework."
        actions={[{ label: 'Open evaluation tools', primary: true, onClick: () => navigate('evaluation') }]}
        note="Survey links and QR resources belong here so pilot evaluation is kept separate from Trust setup."/>
      <TaskCard number="7" icon={QrCode} title="Signposting tools"
        description="Create Journey links, QR codes and printable materials for wards, Emergency Departments and maternity services."
        actions={[{ label: 'Open signposting tools', primary: true, onClick: () => navigate('signposting') }]}/>
    </div>
  </section>;
}

function ClinicalReviewerTasks({ navigate }) {
  return <section className="studio-home-section">
    <div className="studio-home-section-heading">
      <p className="eyebrow">Clinical review</p>
      <h2>Review the content assigned to you</h2>
      <p>Your workspace is limited to the roles, Trust and Journey content within your assigned review scope.</p>
    </div>
    <div className="studio-home-task-grid">
      <TaskCard number="1" icon={ClipboardCheck} title="Review assigned role content"
        description="Check role wording for clinical accuracy, clarity and suitability for patients."
        actions={[{ label: 'Review roles', primary: true, onClick: () => navigate('library') }]}/>
      <TaskCard number="2" icon={LibraryBig} title="Review Journey content"
        description="Check the Journey stages and local configuration within your assigned scope."
        actions={[{ label: 'Review Journeys', primary: true, onClick: () => navigate('journeys') }]}/>
      <TaskCard number="3" icon={ShieldCheck} title="Complete and submit your review"
        description="Resolve outstanding comments and submit the completed review for platform approval."
        actions={[{ label: 'Continue clinical review', primary: true, onClick: () => navigate('library') }]}/>
    </div>
  </section>;
}

function TranslatorTasks({ navigate, access }) {
  const languages = (access?.localeCodes || []).join(', ');
  return <section className="studio-home-section">
    <div className="studio-home-section-heading">
      <p className="eyebrow">Translation review</p>
      <h2>Review your assigned language content</h2>
      <p>{languages ? `Assigned language scope: ${languages}.` : 'Open your translation workspace to see the language scope assigned to you.'}</p>
    </div>
    <div className="studio-home-task-grid">
      <TaskCard number="1" icon={Languages} title="Review translations"
        description="Compare the English source with the translated patient-facing wording and record your decision."
        actions={[{ label: 'Open translation review', primary: true, onClick: () => navigate('translations') }]}/>
      <TaskCard number="2" icon={BookOpenCheck} title="Check context and terminology"
        description="Pay particular attention to healthcare terminology, patient-friendly language and any unnatural English carry-over."
        actions={[{ label: 'Continue translation review', primary: true, onClick: () => navigate('translations') }]}/>
      <TaskCard number="3" icon={ShieldCheck} title="Submit completed review"
        description="When all assigned strings have a decision, submit the language review for platform approval."
        actions={[{ label: 'Review progress', primary: true, onClick: () => navigate('translations') }]}/>
    </div>
  </section>;
}

function PlatformAdminTasks({ navigate }) {
  return <>
    <section className="studio-home-section">
      <div className="studio-home-section-heading">
        <p className="eyebrow">Platform administration</p>
        <h2>Operate and assure Know Your Care</h2>
        <p>Start from the task you need rather than navigating through every Studio workspace.</p>
      </div>
      <div className="studio-home-task-grid">
        <TaskCard number="1" icon={Building2} title="Manage Trusts"
          description="Create, activate, deactivate and inspect Trust profiles."
          actions={[{ label: 'Manage Trusts', primary: true, onClick: () => navigate('trusts') }]}/>
        <TaskCard number="2" icon={UsersRound} title="Manage people and access"
          description="Assign Trust Admin, Clinical Reviewer, Translator and Platform Admin access."
          actions={[{ label: 'People & access', primary: true, onClick: () => navigate('people') }]}/>
        <TaskCard number="3" icon={ClipboardCheck} title="Review submitted clinical content"
          description="Approve reviewer submissions or return them for changes."
          actions={[{ label: 'Clinical review inbox', primary: true, onClick: () => navigate('clinical-reviews') }]}/>
        <TaskCard number="4" icon={Languages} title="Translation assurance"
          description="Review translation work and oversee language coverage."
          actions={[{ label: 'Translations', primary: true, onClick: () => navigate('translations') }]}/>
        <TaskCard number="5" icon={Activity} title="Review readiness and publish"
          description="Run platform checks, inspect Trust readiness, approve releases and control patient access."
          actions={[{ label: 'Pilot readiness', primary: true, onClick: () => navigate('readiness') }]}/>
        <TaskCard number="6" icon={FileText} title="Usage Reports"
          description="Choose any active Trust or site and download its monthly usage report."
          actions={[{ label: 'Open monthly evidence', primary: true, onClick: () => navigate('evidence') }]}/>
        <TaskCard number="8" icon={FileText} title="Print PDF versions of Journeys"
          description="Prepare patient-facing print versions for any configured Trust: Easy Read, large text or standard Journey PDFs."
          actions={[
            { label: 'Easy Read PDFs', onClick: () => navigate('easy-read-print') },
            { label: 'Large text PDFs', onClick: () => navigate('large-text-print') },
            { label: 'Standard Journey PDFs', primary: true, onClick: () => navigate('standard-journey-print') },
          ]}
          note="Review the layout and content before printing or distributing any PDF."/>
        <TaskCard number="9" icon={Shirt} title="Manage national uniform defaults"
          description="Maintain the generic NHS uniform defaults used where a Trust has not supplied a local override."
          actions={[{ label: 'Generic NHS uniforms', primary: true, onClick: () => navigate('generic-uniforms') }]}/>
      </div>
    </section>
    <section className="studio-home-section">
      <div className="studio-home-section-heading">
        <p className="eyebrow">Trust configuration tools</p>
        <h2>Open any Trust setup workspace directly</h2>
      </div>
      <div className="studio-home-quick-links">
        <button onClick={() => navigate('library')}>Roles & uniforms <ArrowRight size={16}/></button>
        <button onClick={() => navigate('uniforms')}>Create / manage uniforms <ArrowRight size={16}/></button>
        <button onClick={() => navigate('hospital-info')}>Hospital information <ArrowRight size={16}/></button>
        <button onClick={() => navigate('journeys')}>Journeys <ArrowRight size={16}/></button>
        <button onClick={() => navigate('evaluation')}>Pilot evaluation tools <ArrowRight size={16}/></button>
        <button onClick={() => navigate('evidence')}>Usage Reports <ArrowRight size={16}/></button>
        <button onClick={() => navigate('signposting')}>Signposting tools <ArrowRight size={16}/></button>
        <button onClick={() => navigate('easy-read-print')}>Easy Read PDFs <ArrowRight size={16}/></button>
        <button onClick={() => navigate('large-text-print')}>Large text PDFs <ArrowRight size={16}/></button>
        <button onClick={() => navigate('standard-journey-print')}>Standard Journey PDFs <ArrowRight size={16}/></button>
      </div>
    </section>
  </>;
}

export default function StudioHome({ studioAccess = null, studioUser = null, studioTrusts = [], onNavigate }) {
  const roles = studioAccess?.roles || [];
  const platformAdmin = roles.includes('platform_admin');
  const trustAdmin = roles.includes('trust_admin');
  const clinicalReviewer = roles.includes('content_reviewer');
  const translator = roles.includes('translator');
  const trustNames = studioTrusts.filter(t => t.id !== 'generic_nhs').map(t => t.shortName || t.name).filter(Boolean);
  const roleText = roles.map(role => roleLabels[role] || role).join(' · ');

  return <div className="studio-home">
    <header className="studio-home-hero">
      <div className="studio-home-brand"><span className="brand-mark"><ShieldCheck/></span><div><strong><StudioBrand /></strong><small>Administration workspace</small></div></div>
      <div className="studio-home-account">
        <div className="studio-home-identity">
        <span>{studioUser?.email || studioAccess?.email || 'Signed in'}</span>
        <strong>{roleText || 'Studio user'}</strong>
        {trustNames.length > 0 && <small>{trustNames.join(' · ')}</small>}
        </div>
        <a className="button secondary studio-home-signout" href="/api/studio-logout"
          aria-label="Sign out of Know Your Care Studio"><LogOut size={16}/> Sign out</a>
      </div>
    </header>
    <main className="studio-home-main">
      <section className="studio-home-intro">
        <p className="eyebrow">Studio home</p>
        <h1>What do you need to do?</h1>
        <p>Choose a task below. Studio will only show tools that are relevant to your assigned role and scope.</p>
      </section>
      {platformAdmin
        ? <PlatformAdminTasks navigate={onNavigate}/>
        : <>
          {trustAdmin && <TrustAdminTasks navigate={onNavigate} allowEasyRead={studioTrusts.some(trust => studioAccess?.trustIds?.includes(trust.id) && trust.active !== false && (trust.easyReadPdfJourneyIds || []).length > 0)}/>}
          {clinicalReviewer && <ClinicalReviewerTasks navigate={onNavigate}/>}
          {translator && <TranslatorTasks navigate={onNavigate} access={studioAccess}/>}
        </>}
    </main>
  </div>;
}
